// =============================================================================
// SEO UTILITIES
// =============================================================================
// Purpose: Centralized SEO helpers for meta tags, descriptions, and analytics
// =============================================================================

// -------------------------------------------------------------------------
// CATEGORY SEO METADATA
// -------------------------------------------------------------------------

/**
 * SEO metadata for each category
 * Includes human-readable names, descriptions, and keywords
 */
export const CATEGORY_SEO = {
  "AI-tools": {
    displayName: "AI Tools",
    title: "Best AI Tools – Free & Trial AI Software",
    description: "Discover the best AI tools for content creation, automation, image generation, and more. Curated free and trial AI software to boost your productivity.",
    keywords: ["AI tools", "artificial intelligence", "AI software", "machine learning tools"],
  },
  "AI-tools ": {
    // Handle trailing space variant
    displayName: "AI Tools",
    title: "Best AI Tools – Free & Trial AI Software",
    description: "Discover the best AI tools for content creation, automation, image generation, and more. Curated free and trial AI software to boost your productivity.",
    keywords: ["AI tools", "artificial intelligence", "AI software", "machine learning tools"],
  },
  "Developer Tools": {
    displayName: "Developer Tools",
    title: "Best Developer Tools – Coding & Programming Software",
    description: "Essential developer tools for coding, debugging, and building software. Find free IDEs, code editors, APIs, and programming utilities.",
    keywords: ["developer tools", "coding tools", "programming software", "IDE", "code editor"],
  },
  "Design-tools": {
    displayName: "Design Tools",
    title: "Best Design Tools – Graphic & UI/UX Design Software",
    description: "Top design tools for graphic design, UI/UX, prototyping, and creative work. Explore free and trial design software for professionals and beginners.",
    keywords: ["design tools", "graphic design", "UI design", "UX tools", "prototyping"],
  },
  "Productivity-tools": {
    displayName: "Productivity Tools",
    title: "Best Productivity Tools – Task Management & Workflow Apps",
    description: "Boost your productivity with the best task management, note-taking, and workflow tools. Discover free apps to organize your work and life.",
    keywords: ["productivity tools", "task management", "workflow apps", "organization tools"],
  },
  "writing-tools": {
    displayName: "Writing Tools",
    title: "Best Writing Tools – AI Writers & Content Creation Software",
    description: "Enhance your writing with AI-powered tools, grammar checkers, and content creation software. Find the best free writing assistants.",
    keywords: ["writing tools", "AI writer", "grammar checker", "content creation"],
  },
  "Entertainment": {
    displayName: "Entertainment",
    title: "Best Entertainment Tools – Media & Fun Applications",
    description: "Discover entertainment tools for media creation, gaming, streaming, and fun. Explore free applications for creative entertainment.",
    keywords: ["entertainment tools", "media tools", "fun apps", "creative tools"],
  },
  "utility-tools": {
    displayName: "Utility Tools",
    title: "Best Utility Tools – Converters, Calculators & Helpers",
    description: "Essential utility tools for everyday tasks. Find free converters, calculators, file tools, and helpful utilities online.",
    keywords: ["utility tools", "converters", "calculators", "online utilities"],
  },
  "All": {
    displayName: "All Tools",
    title: "All Tools – Complete Directory",
    description: "Browse our complete directory of tools across all categories. Find AI, developer, design, productivity, and utility tools in one place.",
    keywords: ["all tools", "tool directory", "software directory"],
  },
};

/**
 * Get SEO metadata for a category
 * @param {string} category - Category name
 * @returns {Object} SEO metadata with title, description, keywords
 */
export function getCategorySEO(category) {
  return CATEGORY_SEO[category] || CATEGORY_SEO["All"];
}

/**
 * Get display-friendly category name
 * @param {string} category - Raw category name
 * @returns {string} Human-readable category name
 */
export function getCategoryDisplayName(category) {
  const seo = getCategorySEO(category);
  return seo.displayName || category;
}

// -------------------------------------------------------------------------
// SEO: RANKING-OPTIMIZED TITLE & DESCRIPTION GENERATORS
// -------------------------------------------------------------------------

/**
 * Generate homepage title (SEO-optimized for discovery searches)
 * @returns {string} SEO-optimized homepage title
 */
export function getHomepageTitle() {
  // SEO: Target discovery keywords + brand
  return "Discover the Best AI, Developer & Productivity Tools – Tolio";
}

/**
 * Generate homepage meta description (CTR-optimized, 140-160 chars)
 * @returns {string} Homepage meta description
 */
export function getHomepageDescription() {
  // SEO: CTR keywords - Free, Curated, Updated + discovery intent
  return "🔍 Find the best free & paid AI, coding, design, and productivity tools. Curated directory with reviews, features & alternatives. Updated daily.";
}

/**
 * Generate category page title (SEO: "Best {Category} Tools & Alternatives")
 * @param {string} category - Category name
 * @returns {string} SEO-optimized category page title
 */
export function getCategoryTitle(category) {
  const seo = getCategorySEO(category);
  // SEO: Format optimized for category/alternative searches
  return `Best ${seo.displayName} & Alternatives (Free & Paid) – Tolio`;
}

/**
 * Generate category page meta description (CTR-optimized, 140-160 chars)
 * @param {string} category - Category name
 * @returns {string} Category meta description
 */
export function getCategoryDescription(category) {
  const seo = getCategorySEO(category);
  // SEO: CTR keywords + discovery intent
  return `✅ Discover the best ${seo.displayName.toLowerCase()} for 2026. Compare features, pricing & alternatives. Curated & updated regularly.`;
}

/**
 * Generate tool page title (SEO: "Tool Overview, Features & Alternatives")
 * Format: "{Tool Name} Tool Overview, Features & Alternatives | Tolio"
 * @param {Object} tool - Tool object with name, tagline, category
 * @returns {string} SEO-optimized tool page title
 */
export function getToolTitle(tool) {
  // SEO: Optimized for tool discovery + comparison searches
  return `${tool.name} Tool Overview, Features & Alternatives | Tolio`;
}

/**
 * Generate tool page meta description (CTR-optimized, 140-160 chars)
 * Includes: overview, features, pricing, alternatives keywords
 * @param {Object} tool - Tool object with name, description, category
 * @returns {string} Tool meta description
 */
export function getToolDescription(tool) {
  const categoryName = getCategoryDisplayName(tool.category) || "tool";
  const pricingText = tool.isFree === true ? "free" : "free & paid";
  
  // SEO: CTR-optimized with discovery intent keywords
  // Format: "Discover {Tool}, {description snippet}. See features, pricing & similar {category} tools."
  if (tool.description && tool.description.length > 30) {
    const shortDesc = tool.description.slice(0, 60).trim();
    return `Discover ${tool.name} – ${shortDesc}... See features, pricing & similar ${categoryName.toLowerCase()}s.`;
  }
  // Fallback with CTR keywords
  return `Discover ${tool.name}, a ${pricingText} ${categoryName.toLowerCase()}. See overview, key features, pricing & similar tools.`;
}

/**
 * Get default tagline based on category
 * @param {string} category - Category name
 * @returns {string} Default tagline
 */
export function getDefaultTagline(category) {
  const taglines = {
    "AI-tools ": "AI-Powered Tool",
    "AI-tools": "AI-Powered Tool",
    "Developer Tools": "Developer Tool",
    "Design-tools": "Design Tool",
    "Productivity-tools": "Productivity Tool",
    "writing-tools": "Writing Assistant",
    "Entertainment": "Entertainment Tool",
    "utility-tools": "Utility Tool",
  };
  return taglines[category] || "Powerful Tool";
}

// -------------------------------------------------------------------------
// META TAG MANAGEMENT
// -------------------------------------------------------------------------

/**
 * Update document meta tags dynamically
 * @param {Object} options - Meta tag options
 * @param {string} options.title - Page title
 * @param {string} options.description - Meta description
 * @param {string} [options.canonicalUrl] - Canonical URL
 * @param {string} [options.ogImage] - Open Graph image URL
 */
export function updateMetaTags({ title, description, canonicalUrl, ogImage }) {
  // Update title
  document.title = title;

  // Update meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement("meta");
    metaDescription.name = "description";
    document.head.appendChild(metaDescription);
  }
  metaDescription.content = description;

  // Update Open Graph tags
  updateOGTag("og:title", title);
  updateOGTag("og:description", description);
  if (ogImage) {
    updateOGTag("og:image", ogImage);
  }

  // Update Twitter tags
  updateMetaTag("twitter:title", title);
  updateMetaTag("twitter:description", description);

  // Update canonical URL
  if (canonicalUrl) {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    updateOGTag("og:url", canonicalUrl);
  }
}

/**
 * Update or create an Open Graph meta tag
 * @param {string} property - OG property name
 * @param {string} content - Tag content
 */
function updateOGTag(property, content) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.content = content;
}

/**
 * Update or create a meta tag
 * @param {string} name - Meta tag name
 * @param {string} content - Tag content
 */
function updateMetaTag(name, content) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.name = name;
    document.head.appendChild(tag);
  }
  tag.content = content;
}

/**
 * Reset meta tags to homepage defaults
 */
export function resetMetaTags() {
  updateMetaTags({
    title: getHomepageTitle(),
    description: getHomepageDescription(),
    canonicalUrl: "https://tolio-web.web.app/",
  });
}

// -------------------------------------------------------------------------
// ANALYTICS HELPERS
// -------------------------------------------------------------------------

/**
 * Track a page view (Google Analytics compatible)
 * @param {string} pagePath - Page path
 * @param {string} pageTitle - Page title
 */
export function trackPageView(pagePath, pageTitle) {
  // Google Analytics 4 (gtag.js)
  if (typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
  // SEO: Log for debugging in development
  if (process.env.NODE_ENV !== "production") {
    console.log(`[Analytics] Page view: ${pagePath} - ${pageTitle}`);
  }
}

/**
 * Track outbound link click (Visit Tool button)
 * @param {string} url - External URL
 * @param {string} toolName - Tool name for labeling
 */
export function trackOutboundClick(url, toolName) {
  // Google Analytics 4 (gtag.js)
  if (typeof window.gtag === "function") {
    window.gtag("event", "click", {
      event_category: "outbound",
      event_label: toolName,
      transport_type: "beacon",
      event_callback: function() {
        // Navigation will happen after tracking
      },
    });
  }
  // SEO: Log for debugging in development
  if (process.env.NODE_ENV !== "production") {
    console.log(`[Analytics] Outbound click: ${toolName} -> ${url}`);
  }
}

/**
 * Track tool card click
 * @param {string} toolName - Tool name
 * @param {string} category - Tool category
 */
export function trackToolClick(toolName, category) {
  if (typeof window.gtag === "function") {
    window.gtag("event", "select_content", {
      content_type: "tool",
      item_id: toolName,
      item_category: category,
    });
  }
}

// -------------------------------------------------------------------------
// URL HELPERS
// -------------------------------------------------------------------------

const BASE_URL = "https://tolio-web.web.app";

/**
 * Get canonical URL for a page
 * @param {string} path - Page path (e.g., "/tool/chatgpt")
 * @returns {string} Full canonical URL
 */
export function getCanonicalUrl(path) {
  return `${BASE_URL}${path}`;
}

// -------------------------------------------------------------------------
// SEO: JSON-LD STRUCTURED DATA (Schema.org)
// -------------------------------------------------------------------------

/**
 * Generate JSON-LD structured data for a tool (SoftwareApplication schema)
 * This improves SERP visibility with rich snippets
 * @param {Object} tool - Tool object
 * @param {string} slug - URL slug for the tool
 * @returns {Object} JSON-LD schema object
 */
export function generateToolSchema(tool, slug) {
  // SEO: Map category to schema applicationCategory
  const categoryMapping = {
    "AI-tools": "Artificial Intelligence",
    "AI-tools ": "Artificial Intelligence",
    "Developer Tools": "DeveloperApplication",
    "Design-tools": "DesignApplication",
    "Productivity-tools": "BusinessApplication",
    "writing-tools": "BusinessApplication",
    "Entertainment": "MultimediaApplication",
    "utility-tools": "UtilitiesApplication",
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": tool.name,
    "description": tool.description || `${tool.name} is a powerful tool for ${getCategoryDisplayName(tool.category)}.`,
    "applicationCategory": categoryMapping[tool.category] || "WebApplication",
    "operatingSystem": "Web Browser, Windows, macOS, Linux",
    "url": getCanonicalUrl(`/tool/${slug}`),
    "offers": {
      "@type": "Offer",
      "price": tool.isFree === true ? "0" : "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/OnlineOnly",
      "description": tool.isFree === true 
        ? "Free to use" 
        : (tool.pricing || "Free tier available, premium plans offered")
    },
    "aggregateRating": tool.rating ? {
      "@type": "AggregateRating",
      "ratingValue": tool.rating,
      "bestRating": "5",
      "worstRating": "1"
    } : undefined,
    "provider": {
      "@type": "Organization",
      "name": "Tolio",
      "url": BASE_URL
    }
  };

  // Remove undefined values
  Object.keys(schema).forEach(key => schema[key] === undefined && delete schema[key]);
  
  return schema;
}

/**
 * Inject JSON-LD script into document head
 * @param {Object} schema - JSON-LD schema object
 * @param {string} id - Unique ID for the script element
 */
export function injectJsonLd(schema, id = "json-ld-tool") {
  // Remove existing script if present
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }

  // Create and inject new script
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.textContent = JSON.stringify(schema, null, 0);
  document.head.appendChild(script);
}

/**
 * Remove JSON-LD script from document head
 * @param {string} id - ID of the script element to remove
 */
export function removeJsonLd(id = "json-ld-tool") {
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }
}

/**
 * Generate slug from tool name
 * @param {string} name - Tool name
 * @returns {string} URL-friendly slug
 */
export function generateSlug(name) {
  if (!name) return "";
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}
